import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { logout } from '@/core/auth/tokenProvider';

/** Public login route users land on after logging out. */
const LOGIN_PATH = '/login';

/**
 * Logs the current user out — awaits `tokenProvider.logout` (calls the backend `POST
 * /auth/logout` endpoint to clear the HttpOnly refresh cookie, then clears the in-memory app
 * token pair) before navigating to the public `/login` route. Replaces the former MSAL
 * `logoutRedirect` call site in `AppHeader`; no outbound redirect happens here (AK7
 * no-redirect-leak guardrail).
 *
 * `tokenProvider.logout` never rejects (it swallows its own network errors so the local store is
 * always cleared), so navigation always follows the logout call — `void` marks the resulting
 * promise as intentionally not further awaited by the caller (e.g. a JSX `onClick` handler),
 * keeping this hook's return type a plain `() => void`.
 *
 * Clears the whole TanStack Query cache once the session is gone: cached responses are
 * actor-specific (e.g. `isOwnComment` on order comments), so a user logging in next in the same
 * tab must never be served the previous user's data from cache.
 */
export function useLogout(): () => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return () => {
    void logout().then(() => {
      queryClient.clear();
      navigate(LOGIN_PATH, { replace: true });
    });
  };
}
